import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, MessageSquare, Megaphone, X, ExternalLink } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SEEN_KEY = "uneksia_seen_announcements";

const TYPE_STYLES = {
  update: {
    bg: "from-purple-500 to-indigo-600",
    icon: Sparkles,
    iconBg: "bg-white/20",
  },
  feedback: {
    bg: "from-emerald-500 to-teal-600",
    icon: MessageSquare,
    iconBg: "bg-white/20",
  },
  marketing: {
    bg: "from-amber-500 to-rose-500",
    icon: Megaphone,
    iconBg: "bg-white/20",
  },
};

/**
 * Floating in-app announcement banner.
 * - Polls /api/announcements/active for the latest active announcement
 *   targeted at the user's current language.
 * - Each user dismisses an announcement once (tracked in localStorage by id).
 * - Mobile: full-width bottom sheet. Desktop: small floating card bottom-right.
 */
export default function AnnouncementBanner() {
  const { i18n } = useTranslation();
  const [announcement, setAnnouncement] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAnnouncement = async () => {
      try {
        const lang = (i18n.language || "fi").slice(0, 2);
        const res = await axios.get(`${API}/announcements/active`, { params: { language: lang } });
        const item = res.data?.announcement;
        if (!item || cancelled) return;

        const seenRaw = localStorage.getItem(SEEN_KEY);
        const seen = seenRaw ? JSON.parse(seenRaw) : [];
        if (Array.isArray(seen) && seen.includes(item.id)) return;

        setAnnouncement(item);
      } catch (e) {
        // silently ignore — banner is non-critical
      }
    };
    fetchAnnouncement();
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  const handleDismiss = () => {
    if (!announcement) return;
    try {
      const seenRaw = localStorage.getItem(SEEN_KEY);
      const seen = seenRaw ? JSON.parse(seenRaw) : [];
      const next = Array.isArray(seen) ? [...new Set([...seen, announcement.id])] : [announcement.id];
      // Keep list reasonable: last 50 dismissed
      localStorage.setItem(SEEN_KEY, JSON.stringify(next.slice(-50)));
    } catch (e) {
      // ignore
    }
    setAnnouncement(null);
  };

  const handleCta = () => {
    if (!announcement?.cta_url) return;
    if (/^https?:\/\//i.test(announcement.cta_url)) {
      window.open(announcement.cta_url, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = announcement.cta_url;
    }
    handleDismiss();
  };

  if (!announcement) return null;

  const style = TYPE_STYLES[announcement.type] || TYPE_STYLES.update;
  const Icon = style.icon;

  return (
    <div
      className="fixed z-[80] left-2 right-2 bottom-2 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-500 safe-bottom"
      data-testid="announcement-banner"
    >
      <div
        className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${style.bg} text-white shadow-2xl`}
      >
        <button
          onClick={handleDismiss}
          aria-label="Sulje"
          className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-white/20 transition-colors z-10"
          data-testid="announcement-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-5 pr-10">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl ${style.iconBg} flex items-center justify-center flex-shrink-0`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3
                className="font-heading text-lg font-medium leading-tight mb-1"
                data-testid="announcement-title"
              >
                {announcement.title}
              </h3>
              <p
                className="font-body text-sm text-white/90 whitespace-pre-wrap break-words"
                data-testid="announcement-message"
              >
                {announcement.message}
              </p>

              {announcement.cta_text && announcement.cta_url && (
                <button
                  onClick={handleCta}
                  className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/20 hover:bg-white/30 text-sm font-body font-medium transition-colors"
                  data-testid="announcement-cta"
                >
                  {announcement.cta_text}
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
